import { useMemo, useState } from "react";
import ActionMenu from "../../actionMenus/action-menu";
import arrowUpIcon from "../../../assets/componentsIcons/listings/arrow_up.svg";
import arrowUpBlueIcon from "../../../assets/componentsIcons/listings/arrow_up_blue.svg";
import dropdownIcon from "../../../assets/componentsIcons/dropdown/dropdown.svg";
import paymentIcon from "../../../assets/Payment-Icon.svg";
import notesIcon from "../../../assets/componentsIcons/dropdown/doc.svg";
import React from "react";
import SimpleSpinner from "../../../components/loader";
import InvoiceIcon from "../../../assets/svgs/payables/arrowDownIcon";
import EmailIcon from "../../../assets/svgs/receivableIcons/emailIcon";
import PhoneIcon from "../../../assets/svgs/receivableIcons/phoneIcon";
import { PaymentDueAccountVM } from "@propertelligent/client-api";
import PayableIcon from "../../../assets/svgs/payableIcon";
import { useNavigate } from "react-router-dom";
import RightSideModal from "../../Modal/right-side-modal";
import WaivePaymentList from "../../payments/WaivePaymentList";
import UtilHelper from "../../../utils/UtilHelper";
import CurrencyTooltip from "../../../components/currencytooltip/CurrencyTooltip";
import editIcon from "../../../assets/componentsIcons/dropdown/edit.svg";
import deleteIcon from "../../../assets/componentsIcons/dropdown/delete.svg";
import { usePermissions } from "../../../hooks/usePermissions";
import { ConfirmationPopup } from "../../confirmation-popup/confirmation-popup-demo";
import apiHelper from "../../../utils/apiHelper";
import { NotificationType, PubSub, TopicsEnum } from "../../../utils/pubsub";
import {
  deletePaymentDueAccountGroup,
  mapDeleteResponseToAction,
  resolveGroupedPaymentIds,
} from "../../../services/paymentDueAccount.service";

type GroupedPaymentDueAccount = PaymentDueAccountVM & {
  paymentDueAccountIds?: number[];
};

interface ReceivableDetailCardProps {
  Id?: any;
  IsFooter?: boolean;
  data?: PaymentDueAccountVM;
  loading?: boolean;
  isBorderRadius?: boolean;
  onRefresh?: () => void;
  onDeleteSuccess?: () => void;
  isEntity?: boolean;
  entityId?: number;
  entityTypeId?: number;
}

const ReceivableDetailCard: React.FC<ReceivableDetailCardProps> = ({
  Id,
  IsFooter = true,
  data,
  loading = false,
  isBorderRadius = true,
  onRefresh,
  onDeleteSuccess,
  isEntity = false,
  entityId,
  entityTypeId
}) => {

  const navigate = useNavigate();
  const { canWrite } = usePermissions();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [tenantId, setTenantId] = useState<number>(0);
  const [paymentToDelete, setPaymentToDelete] =
    useState<GroupedPaymentDueAccount | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const publishDeleteToast = (message: string, type: NotificationType) => {
    PubSub.publish(TopicsEnum.Notification.toString(), {
      message,
      type,
    });
  };

  const refreshAfterDelete = async () => {
    onRefresh?.();
  };

  const handleDelete = async () => {
    if (!paymentToDelete) {
      return;
    }

    const selectedPayment = paymentToDelete;
    setPaymentToDelete(null);
    setIsDeleting(true);

    try {
      const paymentDueAccountIds = resolveGroupedPaymentIds(selectedPayment);

      if (paymentDueAccountIds.length === 0) {
        publishDeleteToast(
          "No payments were available to delete.",
          NotificationType.Warning,
        );
        return;
      }

      const response = await apiHelper(
        deletePaymentDueAccountGroup,
        { showNotification: false },
        paymentDueAccountIds,
      );

      const action = mapDeleteResponseToAction(response);

      if (action.status === "failure") {
        publishDeleteToast(
          "The payment deletion failed. The list has been refreshed.",
          NotificationType.Failure,
        );
        if (action.shouldRefresh) {
          await refreshAfterDelete();
        }
        return;
      }

      publishDeleteToast(
        action.message ?? "",
        action.notificationType === "success"
          ? NotificationType.Success
          : NotificationType.Warning,
      );
      if (action.status === "success" && action.notificationType === "success" && onDeleteSuccess) {
        onDeleteSuccess();
      } else if (action.shouldRefresh) {
        await refreshAfterDelete();
      }
    } finally {
      setIsDeleting(false);
    }
  };

  const getContextParams = () => {
    if (isEntity && entityId) {
      return `&isEntity=${isEntity}&entityId=${entityId}&entityTypeId=${entityTypeId}`;
    }
    return "";
  };

  // Constants for menu items — built inside useMemo so it only recomputes when a dependency changes
  const menuItems = useMemo(() => {
    const items: any[] = [];

    if (canWrite("Payments")) {
      items.push({
        icon: editIcon,
        label: "Edit",
        action: (data: any) =>
          navigate(
            `/payment/edit-receivable/${data?.entityId}?tenantId=${data.entityId}&paymentType=${String(data?.paidFlowTypeId)}${getContextParams()}`,
          ),
        isDivider: false,
        isDestructive: false,
      });

      items.push({ isDivider: true });

      items.push({
        icon: deleteIcon,
        label: "Delete",
        action: (data: GroupedPaymentDueAccount) => setPaymentToDelete(data),
        isDivider: false,
        isDestructive: true,
      });

      items.push({ isDivider: true });

      items.push({
        icon: paymentIcon,
        label: "Post Payment",
        action: (data: any) =>
          navigate(
            `/payment/settle-receivables-payment/${data?.entityId}?tenantId=${data.entityId}&paymentType=${String(data?.paidFlowTypeId)}${getContextParams()}`,
          ),

        isDivider: false,
        isDestructive: false,
      });

      items.push({ isDivider: true });

      items.push({
        icon: paymentIcon,
        label: "Waive Payment",
        customaction: (entityId: number) => {
          setTenantId(entityId);
          setIsModalOpen(true);
        },
        isDivider: false,
        isDestructive: false,
      });
    }

    // Remove trailing dividers
    while (items.length > 0 && items[items.length - 1].isDivider) {
      items.pop();
    }

    return items;
  }, [canWrite, navigate, isEntity, entityId, entityTypeId]);



  if (loading) {
    return <SimpleSpinner myClassName="Icon50" isFullPage={true} />;
  }

  if (!data) {
    return null;
  }



  return (
    <>
      {data.paidFlowTypeId == 1 ? (
        <div
          id={Id}
          className={`listing-card payables ${isBorderRadius ? '' : 'no-border-radius'}`}
        >
          {/* Top section */}
          <div className="listing-card-header">
            <div className="listing-image-container">
              <PayableIcon className="listing-image" width="36px" height="36px" />
            </div>
            <div className="listing-details">
              <div className="listing-info-container">
                <div className="listing-info">
                  <h3 className="listing-address">{data.payerPayeeName}</h3>
                  <p className="listing-type">{data.formatted.PayeeAccountTypeId}</p>
                </div>

                <div className="financial-metrics">
                  <div className="metric">
                    <p className={`metric-value negative`}>
                      ${data.formatted.Amount}
                    </p>
                    <p className="metric-label">Amount</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {IsFooter && (
            <>
              <div className="separator-horizontal" />
              <div className="listing-card-footer">
                <div className="listing-stats">
                  <div className="stat">
                    <InvoiceIcon fill={'#4B5563'} className="stat-icon" width="24" height="24" />
                    <span className="stat-text">
                      Outstanding Invoices: {data.invoicesCount}
                    </span>
                  </div>
                </div>

                <div className="footer-actions">

                  <div className="highlight-value">
                    <img
                      src={arrowUpIcon}
                      alt="Trend"
                      width={16}
                      height={16}
                      className="highlight-icon"
                    />
                    <span className="highlight-text text-danger">Past Due: ${data.formatted.PastDue}</span>
                  </div>

                  <ActionMenu
                    menuItems={menuItems}
                    Id={data.id}
                    trigger={
                      <button className="action-button">
                        <img
                          src={dropdownIcon}
                          alt="More options"
                          width={20}
                          height={20}
                          className="action-icon"
                        />
                      </button>
                    }
                  />
                </div>
              </div>
            </>
          )}
        </div>
      ) : (
        <div id={Id} className="listing-card">
          {/* Top section */}
          <div className="listing-card-header">
            <div className="tenant-avatar">
              <div className="tenant-initials">{UtilHelper.getInitials(data.payerPayeeName)}</div>
            </div>
            <div className="listing-details">
              <div className="listing-info-container">
                <div className="listing-info">
                  <h3 className="listing-address">{data.payerPayeeName}</h3>
                  <p className="listing-type">{data.streetAddress}</p>
                </div>

                <div className="financial-metrics">
                  <div className="metric">
                    <CurrencyTooltip
                      content={data?.formatted?.AmountRaw}
                      formatted={data?.formatted?.Amount}
                    >
                      <p className={`metric-value negative`}>
                        {data.formatted.Amount}
                      </p>
                    </CurrencyTooltip>
                    <p className="metric-label">{"Amount"}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {IsFooter && (
            <>
              <div className="separator-horizontal" />
              <div className="listing-card-footer">
                <div className="listing-stats">
                  {data.email && (
                    <div className="stat">
                      <EmailIcon fill={'#4B5563'} className="stat-icon" width="24" height="24" />
                      <span className="stat-text">
                        {"Email"}: {data.email}
                      </span>
                    </div>
                  )}
                  {data.phoneNumber && (
                    <div className="stat">
                      <PhoneIcon fill={'#4B5563'} className="stat-icon" width="24" height="24" />
                      <span className="stat-text">
                        {"Phone"}: {data.formatted?.PhoneNumber}
                      </span>
                    </div>
                  )}
                </div>

                <div className="footer-actions">
                  {/* Past Due */}
                  {Number(String(data?.formatted?.PastDueRaw).replace(/,/g, "")) > 0 && (
                    <div className="highlight-value negative">
                      <img
                        src={arrowUpIcon}
                        alt="Trend"
                        width={16}
                        height={16}
                        className="highlight-icon"
                        loading="lazy"
                      />
                      <span className="highlight-text">Past Due:{" "}
                        <CurrencyTooltip
                          content={data?.formatted?.PastDueRaw}
                          formatted={data?.formatted?.PastDue}
                        >
                          <span>{data?.formatted?.PastDue}</span>
                        </CurrencyTooltip>
                      </span>
                    </div>
                  )}

                  {/* Due in 30 Days */}
                  {Number(String(data?.formatted?.DueIn30DaysRaw).replace(/,/g, "")) > 0 && (
                    <div className="highlight-value info">
                      <img
                        src={arrowUpBlueIcon}
                        alt="Trend"
                        width={16}
                        height={16}
                        className="highlight-icon"
                        loading="lazy"
                      />
                      <span className="highlight-text">Due in 30 Days:{" "}
                        <CurrencyTooltip
                          content={data?.formatted?.DueIn30DaysRaw}
                          formatted={data?.formatted?.DueIn30Days}
                        >
                          <span>{data?.formatted?.DueIn30Days}</span>
                        </CurrencyTooltip>
                      </span>
                    </div>
                  )}

                  {menuItems.length > 0 && (
                    <ActionMenu
                      menuItems={menuItems}
                      Id={data}
                      entityId={data?.entityId}
                      trigger={
                        <button className="action-button">
                          <img
                            src={dropdownIcon}
                            alt="More options"
                            width={20}
                            height={20}
                            className="action-icon"
                          />
                        </button>
                      }
                    />
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      )}
      {tenantId > 0 && (
        <RightSideModal
          isOpen={isModalOpen}
          onClose={() => {
            setIsModalOpen(false);
            setTenantId(0);
          }}
          title="Waive Payment"
          isFooterHidden={false}
          buttonlabel="Waive Payment"
          formId="WaivePayment"
          maxWidth={500}>
          <WaivePaymentList
            tenantId={tenantId}
            setIsModalOpen={(value: boolean) => {
              setIsModalOpen(value);
              if (!value) setTenantId(0);
            }}
            fetchData={() => {
              if (onRefresh) onRefresh();
            }}
          />
        </RightSideModal>
      )}
      <ConfirmationPopup
        isOpen={paymentToDelete !== null}
        title="Delete Payment"
        message="Are you sure you want to delete this payment? Only payments in Due status will be deleted."
        primaryButtonLabel="Delete"
        secondaryButtonLabel="Cancel"
        onPrimaryAction={handleDelete}
        onSecondaryAction={() => setPaymentToDelete(null)}
        onClose={() => setPaymentToDelete(null)}
        isPrimaryDestructive={true}
        isLoading={isDeleting}
        loadingLabel="Deleting..."
      />
    </>
  );
};

export default ReceivableDetailCard;